import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  forwardRef,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HxBaseControl } from '../../../base/hx-base-control';
import { HxError, HxFormGroup, HxHint } from '../../form-group/index';
import { HxInput } from '../../input';
import { HxLabel } from '../../label/label';
import type { HxTagSize } from '../../tag/tag';
import { HxTagComponent } from '../../tag/tag';
import { HxDropdownComponent } from '../dropdown/dropdown.component';
import { HxOptionGroup } from '../index';
import { createOption, isOtherOption, type HxOption } from '../shared/option.model';
import type { HxSelectPosition, HxSelectSize } from '../shared/select-types';

/**
 * HxDropdownWithLabel - Composition wrapper component that combines HxFormGroup, HxLabel, HxDropdown, HxHint, and
 * HxError to eliminate boilerplate code.
 *
 * This component provides:
 *
 * - Automatic label association
 * - Automatic error detection from FormControl
 * - Pass-through API to HxDropdown
 * - ControlValueAccessor for Reactive Forms integration
 * - WCAG 2.1 AA accessibility compliance
 *
 * @example
 *   ```html
 *   <hx-dropdown-with-label
 *     label="Country"
 *     [required]="true"
 *     [options]="countries"
 *     placeholder="Select a country"
 *     hint="Choose your country of residence"
 *     [formControl]="countryControl">
 *     @if (countryControl.errors?.['required']) {
 *       <div hx-error>Country is required</div>
 *     }
 *   </hx-dropdown-with-label>
 *   ```;
 */
@Component({
  selector: 'hx-dropdown-with-label',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ScrollingModule,
    HxFormGroup,
    HxLabel,
    HxHint,
    HxError,
    HxDropdownComponent,
    HxTagComponent,
    HxInput,
  ],
  templateUrl: './dropdown-with-label.component.html',
  styleUrl: './dropdown-with-label.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': '_alignmentClass()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxDropdownWithLabelComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HxDropdownWithLabelComponent),
      multi: true,
    },
  ],
})
export class HxDropdownWithLabelComponent<T = any> extends HxBaseControl implements DoCheck {
  // --- Wrapper-Specific Inputs ---

  /**
   * Label text displayed above the dropdown
   *
   * @required
   */
  readonly label = input<string>('');

  /**
   * Subtitle text displayed below the label
   *
   * @optional
   */
  readonly subtitle = input<string>('');

  /**
   * Helper text displayed below the dropdown (replaced by errors when invalid)
   *
   * @optional
   */
  readonly hint = input<string | undefined>(undefined);

  /**
   * Horizontal alignment of the dropdown and label within the container
   *
   * - 'left': Align to the left (default)
   * - 'center': Align to the center
   * - 'right': Align to the right
   *
   * @default 'left'
   */
  readonly justified = input<'left' | 'center' | 'right'>('left');

  /**
   * Whether to show required indicator (red asterisk) after label
   *
   * @default false
   */

  /**
   * Whether the dropdown is disabled
   *
   * @default false
   */
  override readonly disabled = input<boolean | undefined>(false);

  // --- Pass-Through Inputs from HxDropdown ---

  /**
   * Array of options or option groups to display in dropdown
   *
   * @required
   */
  readonly options = input.required<(HxOption<T> | HxOptionGroup<T>)[]>();

  /**
   * Placeholder text shown when no value selected
   *
   * @default 'Select an option'
   */
  readonly placeholder = input<string>('Select an option');

  /** Custom CSS classes applied to the dropdown overlay panel. */
  readonly panelClass = input<string>('');

  /**
   * Component size variant
   *
   * @default 'md'
   */
  readonly size = input<HxSelectSize>('md');

  /**
   * Dropdown panel position relative to trigger
   *
   * @default 'bottom-start'
   */
  readonly position = input<HxSelectPosition>('bottom-start');

  /**
   * Selection mode - determines if user can select one or multiple options
   *
   * - 'single': User can select only one option
   * - 'multi': User can select multiple options
   *
   * @default 'single'
   */
  readonly selectionMode = input<'single' | 'multi'>('single');

  /**
   * Enable multiple selection mode
   *
   * @deprecated Use selectionMode instead
   * @default false
   */
  readonly multiple = input<boolean>(false);

  /** Computed signal for multiple selection mode Derives from selectionMode or falls back to multiple input */
  protected readonly _isMultipleMode = computed(() => {
    return this.selectionMode() === 'multi' || this.multiple();
  });

  /** Minimum number of selections required (multi-select only) */
  readonly minSelections = input<number | undefined>(undefined);

  /** Maximum number of selections allowed (multi-select only) */
  readonly maxSelections = input<number | undefined>(undefined);

  /**
   * Enable search/filter functionality
   *
   * @default false
   */
  readonly searchable = input<boolean>(false);

  /**
   * Allow committing free-text values (single-select only).
   *
   * @default false
   */
  readonly allowFreeText = input<boolean>(false);

  /**
   * Show selected items as tags below the dropdown (multi-select only)
   *
   * @default false
   */
  readonly showTags = input<boolean>(false);

  /**
   * Whether to show a tooltip on the dropdown trigger button when hovering
   *
   * @default true
   */
  readonly showTriggerTooltip = input<boolean>(true);

  /**
   * Label text displayed above the tags section (multi-select only)
   *
   * @default null
   * @optional
   */
  readonly tagsLabel = input<string | null>(null);

  readonly tagVariant = input<'filled' | 'soft'>('filled');

  /** Selected-tags row uses the same size as the dropdown itself ('xl' has no tag equivalent, falls back to 'lg'). */
  protected readonly _tagSize = computed<HxTagSize>(() => {
    const size = this.size();
    return size === 'xl' ? 'lg' : size;
  });

  /**
   * Show "Select All" checkbox for multi-select mode Allows users to select or deselect all options at once
   *
   * @default false
   */
  readonly showSelectAll = input<boolean>(false);

  /**
   * Enable virtual scrolling for large lists Improves performance when rendering hundreds or thousands of options
   *
   * @default false
   */
  readonly virtualScroll = input<boolean>(false);

  /**
   * Item height in pixels for virtual scrolling Used to calculate viewport size and scroll position
   *
   * @default 40
   */
  readonly itemSize = input<number>(40);

  /**
   * Enable "Other" option functionality When enabled:
   *
   * - Automatically adds an "Other" option to the dropdown
   * - Shows an input field when "Other" is selected
   * - Allows user to enter custom value
   *
   * @default false
   */
  readonly showOtherInput = input<boolean>(false);

  /**
   * Value that triggers the "other" input field
   *
   * @default 'other'
   */
  readonly otherValue = input<string>('other');

  /**
   * Label for the "other" input field
   *
   * @default 'Please specify'
   */
  readonly otherLabel = input<string>('Please specify');

  /**
   * Placeholder for the "other" input field
   *
   * @default 'Enter details...'
   */
  readonly otherPlaceholder = input<string>('Enter details...');

  /**
   * When true, a synthetic All option is added to the options list. Selecting it selects all standard options;
   * deselecting it deselects them all.
   */
  readonly showAllOption = input<boolean>(false);

  /** Display label for the synthetic All option */
  readonly allLabel = input<string>('All');

  /**
   * When true, a synthetic None option is added to the options list. Selecting it clears all other selections (mutually
   * exclusive).
   */
  readonly showNoneOption = input<boolean>(false);

  /** Display label for the synthetic None option */
  readonly noneLabel = input<string>('None');

  /**
   * FormControl for the "Other" input field. Parent must create and manage this control for validation.
   *
   * @default undefined (no external control)
   */
  readonly otherInputControl = input<FormControl<string> | null>(null);

  /** Disable the "Other" option (e.g. driven by external Set Rules) */
  readonly otherDisabled = input<boolean>(false);

  /** Hide the "Other" option entirely (e.g. driven by external Set Rules) */
  readonly otherHidden = input<boolean>(false);

  /** Multiline textarea for Other input instead of single-line input */
  readonly otherInputMultiline = input<boolean>(false);

  /** Full properties of the nested "Other" text-input child subcontrol */
  readonly otherInputProps = input<Record<string, any> | null>(null);

  /** Custom error message for required validation */
  readonly requiredErrorMessage = input<string | undefined>(undefined);

  /** Custom error message for required "other" input validation */
  readonly otherRequiredErrorMessage = input<string | undefined>(undefined);

  /** Custom error message for min selections */
  readonly minSelectionErrorMessage = input<string | undefined>(undefined);

  /** Custom error message for max selections */
  readonly maxSelectionErrorMessage = input<string | undefined>(undefined);

  // --- Component Outputs ---

  /** Emitted when dropdown selection changes Value type: T (single) | T[] (multiple) | null (cleared) */
  readonly valueChange = output<T | T[] | null>();

  /** Emitted when dropdown panel opens or closes Value: true (opened) | false (closed) */
  readonly openChange = output<boolean>();

  // --- Internal State ---

  /** Internal FormControl for ControlValueAccessor delegation Binds to child HxDropdown via [formControl] directive */
  protected readonly _internalControl = new FormControl<T | T[] | null>(null);

  /** Reactive signal for invalid state */
  public readonly _invalid = signal<boolean>(false);

  /** Reactive signal for touched state */
  public readonly _touched = signal<boolean>(false);

  /** Tracks the previous touched state of external control. Used in DoCheck to detect programmatic touch changes. */
  private _previousExternalTouched = false;

  /** Reactive signal for current control value (for tag display) */
  protected readonly _currentValue = signal<T | T[] | null>(null);

  /** Reactive signal for current control errors. FormControl.errors itself is not signal-aware. */
  protected readonly _controlErrors = signal<ValidationErrors | null>(null);

  /** Reactive signal for the current Other input text. */
  protected readonly _otherInputValue = signal('');

  /** Computed signal for error state */
  protected readonly _hasError = computed(() => this.error() || (this._invalid() && this._touched()));

  /** Computed list of active error message strings — passed to hx-form-group's errorMessages input */
  protected readonly _activeErrorMessage = computed(() => {
    if (!this._hasError()) return '';
    const errors = this._controlErrors();
    if (!errors) return '';
    // Only one error shown at a time, in priority order
    if (errors['required']) return this.getValidationMessage('required');
    if (errors['minSelection']) return this.getValidationMessage('minSelection');
    if (errors['maxSelection']) return this.getValidationMessage('maxSelection');
    return '';
  });

  /** Internal FormControl for "Other" input field */
  protected readonly _otherInputControl = new FormControl<string>('');

  /** Computed signal for "other" field error state */
  protected readonly _otherFieldHasError = computed(() => {
    // Only show error if showOtherInput is enabled
    if (!this.showOtherInput()) {
      return false;
    }
    const otherSelected = this._isOtherSelected();
    const otherEmpty = this._otherInputValue().trim() === '';
    return !!(otherSelected && otherEmpty && (this.error() || this._touched()));
  });

  /** Computed signal for "other" field error message */
  protected readonly _otherErrorMessage = computed(() => {
    if (this._otherFieldHasError()) {
      return this.otherRequiredErrorMessage() || 'Please specify a value for "Other"';
    }
    return '';
  });

  /** Computed signal for options including "Other" option when showOtherInput is enabled */
  protected readonly _optionsWithOther = computed(() => {
    let baseOptions = this.options();

    if (this.showNoneOption()) {
      const hasNone = baseOptions.some((opt) => 'value' in opt && (opt.value === 'none' || opt.id === 'none'));
      if (!hasNone) {
        const noneOption = createOption('none', this.noneLabel(), 'none' as unknown as T);
        baseOptions = [noneOption, ...baseOptions];
      }
    }

    if (this.showAllOption()) {
      const hasAll = baseOptions.some((opt) => 'value' in opt && (opt.value === 'all' || opt.id === 'all'));
      if (!hasAll) {
        const allOption = createOption('all', this.allLabel(), 'all' as unknown as T);
        const noneIdx = baseOptions.findIndex((opt) => 'value' in opt && (opt.value === 'none' || opt.id === 'none'));
        if (noneIdx >= 0) {
          baseOptions = [...baseOptions.slice(0, noneIdx + 1), allOption, ...baseOptions.slice(noneIdx + 1)];
        } else {
          baseOptions = [allOption, ...baseOptions];
        }
      }
    }
    if (!this.showOtherInput()) return baseOptions;

    const otherValue = this.otherValue();
    const filteredOptions = this.removeOtherOption(baseOptions, otherValue);

    if (this.otherHidden()) {
      return filteredOptions;
    }

    // Add "Other" option at the end
    const otherOption = createOption(String(this.otherValue()), 'Other', otherValue, {
      disabled: this.otherDisabled(),
    });
    return [...filteredOptions, otherOption] as (HxOption<T> | HxOptionGroup<T>)[];
  });

  /** Computed CSS class for alignment */
  protected readonly _alignmentClass = computed(() => {
    const alignment = this.justified();
    return `hx-dropdown-with-label--${alignment}`;
  });

  /** Computed signal to check if "Other" option is currently selected */
  protected readonly _isOtherSelected = computed(() => {
    if (!this.showOtherInput()) return false;
    const value = this._currentValue();
    const otherVal = this.otherValue();

    if (this._isMultipleMode()) {
      return Array.isArray(value) && value.some((selectedValue) => selectedValue === otherVal);
    } else {
      return value === otherVal;
    }
  });

  private _isOtherSelectedValue(value: T | T[] | null): boolean {
    if (!this.showOtherInput()) return false;

    const otherVal = this.otherValue();
    if (this._isMultipleMode()) {
      return Array.isArray(value) && value.some((selectedValue) => selectedValue === otherVal);
    }

    return value === otherVal;
  }

  /** Computed signal for selected items as displayable tags */
  protected readonly _selectedTags = computed(() => {
    if (!this._isMultipleMode() || !this.showTags()) return [];

    const value = this._currentValue();
    if (!Array.isArray(value) || value.length === 0) return [];

    // Flatten all options to find labels
    const flatOptions: HxOption<T>[] = [];
    this.options().forEach((item) => {
      if ('options' in item) {
        flatOptions.push(...(item as HxOptionGroup<T>).options);
      } else {
        flatOptions.push(item as HxOption<T>);
      }
    });

    // When the Other text input is visible, suppress the Other chip to avoid duplication
    const hideOtherChip = this.showOtherInput() && this._isOtherSelected();
    const otherVal = this.otherValue();

    // Map selected values to their labels
    return value
      .map((val) => {
        if (hideOtherChip && val === otherVal) return null;
        const option = flatOptions.find((opt) => opt.value === val);
        return option ? { value: val, label: option.label } : null;
      })
      .filter((tag): tag is { value: T; label: string } => tag !== null);
  });

  /** Effect to handle disabled state changes */
  private readonly _disabledStateEffect = effect(() => {
    const isDisabled = this.disabledState();
    if (isDisabled && this._internalControl.enabled) {
      this._internalControl.disable({ emitEvent: false });
    } else if (!isDisabled && this._internalControl.disabled) {
      this._internalControl.enable({ emitEvent: false });
    }
  });

  /** Effect to handle pre-selection from isSelected property on options */
  private readonly _preSelectionEffect = effect(() => {
    const opts = this.options();

    // Flatten options and get selected values from isSelected property
    const flatOpts: HxOption<T>[] = [];
    opts.forEach((item) => {
      if ('options' in item) {
        flatOpts.push(...(item as HxOptionGroup<T>).options);
      } else {
        flatOpts.push(item as HxOption<T>);
      }
    });

    const selectedValues = flatOpts.filter((opt) => opt.isSelected).map((opt) => opt.value);

    // Only set initial value if control is empty and there are selected options
    if (selectedValues.length > 0 && !this._internalControl.value) {
      const value = this._isMultipleMode() ? selectedValues : selectedValues[0];
      this._internalControl.setValue(value as T | T[], { emitEvent: false });
      this._currentValue.set(value as T | T[] | null);
    }
  });

  /** Effect to sync FormControl validators when inputs change */
  private readonly _validatorSyncEffect = effect(() => {
    // Trigger effect when these inputs change
    this.minSelections();
    this.maxSelections();
    this.required();
    this._isMultipleMode();
    this.showOtherInput();
    this.otherValue();
    this.otherInputControl();

    this._internalControl.setValidators(this._buildValidators());
    this._internalControl.updateValueAndValidity({ emitEvent: false });
    this._invalid.set(this._internalControl.invalid);
    this._controlErrors.set(this._internalControl.errors);
    this._validatorChangeFn();
  });

  /** Keep the auxiliary Other input wired to the current control instance. */
  private readonly _otherControlSyncEffect = effect((onCleanup) => {
    const otherControl = this.otherInputControl() || this._otherInputControl;

    this._otherInputValue.set(otherControl.value ?? '');

    const otherControlSub = otherControl.valueChanges.subscribe(() => {
      this._otherInputValue.set(otherControl.value ?? '');
      this._internalControl.updateValueAndValidity({ emitEvent: false });
      this._invalid.set(this._internalControl.invalid);
      this._controlErrors.set(this._internalControl.errors);
      this._validatorChangeFn();
    });

    onCleanup(() => otherControlSub.unsubscribe());
  });

  /**
   * Effect to re-validate when "Other" selection state changes. This ensures the _otherFieldValidator is properly
   * evaluated when the user selects or deselects "Other" from the dropdown. This is critical for backward
   * compatibility
   *
   * - Existing templates with "Other" options will now properly validate the text field without requiring explicit
   *   configuration.
   */
  private readonly _otherSelectionValidationEffect = effect(() => {
    // Watch the _isOtherSelected computed signal
    const isOtherSelected = this._isOtherSelected();
    // Also watch showOtherInput to handle auto-detected "Other" options
    const showOther = this.showOtherInput();

    // When "Other" is selected or deselected, re-run validation
    if (showOther) {
      this._internalControl.updateValueAndValidity({ emitEvent: false });
      this._invalid.set(this._internalControl.invalid);
      this._controlErrors.set(this._internalControl.errors);
      this._validatorChangeFn();
    }
  });

  /** A rule can disable/hide "Other" while it's selected - deselect it reactively */
  private readonly _otherRuleEffect = effect(() => {
    if ((this.otherDisabled() || this.otherHidden()) && this._isOtherSelected()) {
      const otherVal = this.otherValue();
      const current = this._internalControl.value;
      let next: T | T[] | null;
      if (Array.isArray(current)) {
        const remaining = current.filter((v) => v !== otherVal) as T[];
        next = remaining.length > 0 ? remaining : null;
      } else {
        next = current === otherVal ? null : current;
      }
      this._internalControl.setValue(next);
    }
  });

  /** Callback for touched state (called when dropdown loses focus) */

  // --- ControlValueAccessor Implementation ---

  override ngOnInit(): void {
    super.ngOnInit();

    // Register as value accessor manually to avoid circular dependency
    // (NG_VALUE_ACCESSOR provider + NgControl injection causes circular DI)
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Initialize signals with current FormControl state
    this._invalid.set(this._internalControl.invalid);
    this._touched.set(this._internalControl.touched);
    this._currentValue.set(this._internalControl.value);
    this._controlErrors.set(this._internalControl.errors);

    // Subscribe to FormControl value changes
    this._internalControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      this._onChange(value);
      this.valueChange.emit(value);
      // IMPORTANT: Update _currentValue BEFORE reading validation state.
      // The _otherFieldValidator depends on _isOtherSelected() which reads _currentValue().
      // If we read _invalid before updating _currentValue, the validator will use stale data.
      this._currentValue.set(value);
      // Re-run validation now that _currentValue is updated (for "Other" field validation)
      this._internalControl.updateValueAndValidity({ emitEvent: false });
      this._invalid.set(this._internalControl.invalid);
      this._controlErrors.set(this._internalControl.errors);
    });

    // Subscribe to FormControl status changes
    this._internalControl.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._invalid.set(this._internalControl.invalid);
      this._controlErrors.set(this._internalControl.errors);
    });

    // Connect external control state
    this.connectExternalControlState(this._internalControl, {
      setInvalid: (invalid) => this._invalid.set(invalid),
      setTouched: (touched) => this._touched.set(touched),
    });

    // Initialize previous touched state from external control
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      this._previousExternalTouched = externalControl.touched;
    }
  }

  /**
   * DoCheck lifecycle hook to detect programmatic touch state changes. This is critical for handling
   * form.markAllAsTouched() which doesn't trigger statusChanges.
   */
  ngDoCheck(): void {
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      const currentTouched = externalControl.touched;
      // If touched state changed externally (e.g., form.markAllAsTouched())
      if (currentTouched !== this._previousExternalTouched) {
        this._previousExternalTouched = currentTouched;
        this._touched.set(currentTouched);
        // Also mark the other input control as touched if it exists
        if (currentTouched && this._isOtherSelected()) {
          const otherControl = this.otherInputControl() || this._otherInputControl;
          otherControl?.markAsTouched();
        }
      }
    }
  }
  /**
   * Write value from parent FormControl to internal control Called by Angular forms when control value changes
   * externally
   */
  writeValue(value: T | T[] | null): void {
    this._internalControl.setValue(value, { emitEvent: false });
    this._invalid.set(this._internalControl.invalid);
    this._currentValue.set(value);
    this._controlErrors.set(this._internalControl.errors);
  }

  /** Register callback for value changes Called when internal control value changes (user interaction) */
  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this._internalControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(fn);
  }

  /** Register callback for touched state Called when dropdown is blurred/closed */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** Set disabled state on internal control Called by Angular forms when control.disable() or control.enable() is called */
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this._internalControl.disable({ emitEvent: false });
    } else {
      this._internalControl.enable({ emitEvent: false });
    }
  }

  // ============================================================================
  // VALIDATOR IMPLEMENTATION
  // ============================================================================

  validate(_: AbstractControl): ValidationErrors | null {
    return this._internalControl.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this._validatorChangeFn = fn;
  }

  /** Handle dropdown value changes (bubble up to parent) */
  protected _onValueChange(value: T | T[] | null): void {
    // Mark touched on value change so Other-required validation behaves consistently for single and multi select.
    if (this._isMultipleMode() || this._isOtherSelectedValue(value)) {
      this._internalControl.markAsTouched();
      this._touched.set(true);
      this._onTouched();
    }
    this.valueChange.emit(value);
  }

  /** Handle dropdown open/close state changes */
  protected _onOpenChange(isOpen: boolean): void {
    if (!isOpen) {
      this._internalControl.markAsTouched();
      this._touched.set(true);
      // Force update invalid state when closing
      this._invalid.set(this._internalControl.invalid);
      this._onTouched();
    }
    this.openChange.emit(isOpen);
  }

  /** Handle tag removal from multi-select */
  protected _onTagRemove(valueToRemove: T): void {
    const currentValue = this._internalControl.value;
    if (Array.isArray(currentValue)) {
      const newValue = currentValue.filter((val) => val !== valueToRemove);
      this._internalControl.setValue(newValue);
      this._internalControl.markAsTouched();
      this._touched.set(true);
    }
  }

  /** Handle blur event on "other" input field */
  protected _handleOtherBlur(): void {
    const otherControl = this.otherInputControl() || this._otherInputControl;
    otherControl?.markAsTouched();
    this._internalControl.markAsTouched();
    this._touched.set(true);
  }

  /** Builds validator array based on component inputs */
  private _buildValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    const isMultiple = this._isMultipleMode();
    const min = this.minSelections();
    const max = this.maxSelections();
    const hasMinMax = (min != null && min > 0) || max != null;

    // For multi-select with minSelections > 0, skip required validator (minSelection handles empty state)
    if (this.required() && !(isMultiple && min != null && min > 0)) {
      validators.push(Validators.required);
    }

    // Only apply min/max validation for multi-select
    if (isMultiple && hasMinMax) {
      validators.push(this._minMaxValidator.bind(this));
    }

    if (this.showOtherInput()) {
      validators.push(this._otherFieldValidator.bind(this));
    }

    return validators;
  }

  /** Validator that enforces non-empty text when the Other option is selected. */
  private _otherFieldValidator(_: AbstractControl): ValidationErrors | null {
    if (!this.showOtherInput() || !this._isOtherSelected()) {
      return null;
    }

    const otherControl = this.otherInputControl() || this._otherInputControl;
    const value = otherControl?.value?.trim() ?? '';
    return value ? null : { otherRequired: true };
  }

  /** Custom validator for min/max selection constraints */
  private _minMaxValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as T | T[] | null;
    const length = Array.isArray(value) ? value.length : value != null ? 1 : 0;
    const min = this.minSelections();
    const max = this.maxSelections();

    // Check max constraint first to prioritize showing "too many" over "too few"
    if (max != null && length > max) {
      return {
        maxSelection: {
          required: max,
          actual: length,
        },
      };
    }

    // Check min constraint (only if min > 0)
    if (min != null && min > 0 && length < min) {
      return {
        minSelection: {
          required: min,
          actual: length,
        },
      };
    }

    return null;
  }

  /** Removes any existing Other option so it can be appended exactly once at the end. */
  private removeOtherOption(
    options: (HxOption<T> | HxOptionGroup<T>)[],
    otherValue: string
  ): (HxOption<T> | HxOptionGroup<T>)[] {
    return options
      .map((item) => {
        if (!('options' in item)) {
          return isOtherOption(item, otherValue) ? null : item;
        }

        const filteredGroupOptions = item.options.filter((option) => !isOtherOption(option, otherValue));
        return filteredGroupOptions.length > 0
          ? ({ ...item, options: filteredGroupOptions } as HxOptionGroup<T>)
          : null;
      })
      .filter((item): item is HxOption<T> | HxOptionGroup<T> => item !== null);
  }

  /** Gets error message for display */
  protected _getErrorMessage(errorType: 'required' | 'otherRequired' | 'minSelection' | 'maxSelection'): string {
    const errors = this._internalControl.errors;
    if (!errors) return '';

    switch (errorType) {
      case 'required':
        return this.requiredErrorMessage() || 'This field is required';

      case 'otherRequired':
        return this.otherRequiredErrorMessage() || 'Please specify a value for "Other"';

      case 'minSelection':
        return this.minSelectionErrorMessage() || `Please select at least ${errors['minSelection'].required} option(s)`;

      case 'maxSelection':
        return (
          this.maxSelectionErrorMessage() || `Please select no more than ${errors['maxSelection'].required} option(s)`
        );

      default:
        return '';
    }
  }

  /** Public method to get error messages */
  getValidationMessage(errorType: 'required' | 'otherRequired' | 'minSelection' | 'maxSelection'): string {
    return this._getErrorMessage(errorType);
  }

  /**
   * Public getter for accessing internal control errors in templates Use this to check for min/max selection validation
   * errors
   */
  get errors(): ValidationErrors | null {
    return this._internalControl.errors;
  }
}
